import { useMemo, useState } from 'react'
import { chercher } from '../lib/search.ts'
import { magasin } from '../lib/client/store.ts'
import {
  useMagasin,
  type Article,
  type Lieu,
  type Produit,
  type Rayon,
  type Peremption,
} from '../lib/client/hooks.ts'
import { Sheet } from '../components/Sheet.tsx'
import { Croix } from '../components/Icone.tsx'
import { BadgeDlc, joursAvant } from '../components/BadgeDlc.tsx'
import { annoncer } from '../components/Toast.tsx'
import { LIBELLE } from '../components/StateGauge.tsx'
import type { EtatStock } from '../lib/ops/index.ts'

const ETATS: EtatStock[] = ['en_stock', 'bientot_fini', 'epuise']

/**
 * Détail d'un article : c'est ici qu'on fait tout ce que la jauge ne fait
 * pas — déplacer, dater, annoter, et RETIRER.
 *
 * Le manque était réel : on pouvait passer un article à « épuisé », mais pas
 * le sortir de l'inventaire. Or les deux gestes ne disent pas la même chose.
 * « Épuisé » veut dire « il en faut » — ça alimente la liste de courses.
 * « Retirer » veut dire « on n'en suit plus » — le pot d'épices exotique
 * acheté une fois, la boîte qu'on ne rachètera pas.
 *
 * La suppression n'ouvre PAS de dialogue de confirmation. Elle agit tout de
 * suite et propose d'annuler par un toast : un dialogue qu'on voit souvent
 * finit validé sans être lu, et il coûte un geste les neuf fois sur dix où
 * l'on était sûr de soi.
 */
export function DetailArticle({
  articleId,
  onFermer,
}: {
  articleId: string | null
  onFermer: () => void
}) {
  const { etat } = useMagasin()
  const [nouvelleDate, setNouvelleDate] = useState('')
  const [fusion, setFusion] = useState('')

  const vivant = <T,>(m: Map<string, unknown>) =>
    [...m.values()].filter((l) => !(l as { deletedAt?: unknown }).deletedAt) as T[]

  const article = articleId
    ? (vivant<Article>(etat.inventoryItems).find((a) => a.id === articleId) ?? null)
    : null
  const produit = article
    ? (vivant<Produit>(etat.catalogItems).find((p) => p.id === article.catalogItemId) ?? null)
    : null
  const lieux = useMemo(
    () => vivant<Lieu>(etat.locations).sort((a, b) => a.sortOrder - b.sortOrder),
    [etat],
  )
  const rayons = useMemo(
    () => vivant<Rayon>(etat.rayons).sort((a, b) => a.sortOrder - b.sortOrder),
    [etat],
  )
  const dates = article
    ? vivant<Peremption>(etat.expiries)
        .filter((p) => p.inventoryItemId === article.id)
        .sort((a, b) => a.date.localeCompare(b.date))
    : []

  const lieuActuel = lieux.find((l) => l.id === article?.locationId)

  // Tous les produits SAUF celui-ci : on ne se fusionne pas avec soi-même.
  const candidats = useMemo(
    () => vivant<Produit>(etat.catalogItems).filter((p) => p.id !== produit?.id),
    [etat, produit?.id],
  )

  function supprimer() {
    if (!article || !produit) return
    const { catalogItemId, locationId } = article
    magasin.dispatch('item.delete', { id: article.id })
    onFermer()
    annoncer(`${produit.name} retiré de l'inventaire`, {
      label: 'Annuler',
      faire: () =>
        /*
         * L'annulation recrée l'article plutôt que de « dé-supprimer » : une
         * pierre tombale ne se relève pas — c'est ce qui garantit qu'une
         * suppression ne ressuscite jamais toute seule au retour d'un
         * téléphone resté hors-ligne. Recréer redonne le même produit au
         * même endroit, ce qui est tout ce que la personne voulait.
         */
        magasin.dispatch('item.create', {
          id: magasin.id(),
          catalogItemId,
          locationId,
          state: article.state,
        }),
    })
  }

  return (
    <Sheet ouverte={!!article} onFermer={onFermer} titre={produit?.name ?? ''}>
      {article && produit && (
        <>
          <section className="mb-5">
            <h3 className="mb-2 text-label uppercase text-muted">État</h3>
            <div className="flex gap-2">
              {ETATS.map((e) => (
                <button
                  key={e}
                  type="button"
                  aria-pressed={article.state === e}
                  onClick={() =>
                    magasin.dispatch('item.setState', {
                      id: article.id,
                      state: e,
                      shoppingId: e === 'epuise' ? magasin.id() : undefined,
                    })
                  }
                  className={`pressable h-11 flex-1 rounded-sm text-meta ${
                    article.state === e
                      ? e === 'epuise'
                        ? 'bg-epuise text-canvas'
                        : e === 'bientot_fini'
                          ? 'bg-bientot text-ink'
                          : 'bg-ink text-canvas'
                      : 'bg-surface-sunk text-ink'
                  }`}
                >
                  {LIBELLE[e]}
                </button>
              ))}
            </div>
          </section>

          <section className="mb-5">
            <h3 className="mb-2 text-label uppercase text-muted">Où c'est rangé</h3>
            <div className="flex flex-wrap gap-2">
              {lieux.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  aria-pressed={l.id === article.locationId}
                  onClick={() => magasin.dispatch('item.move', { id: article.id, locationId: l.id })}
                  className={`pressable h-10 rounded-full px-3 text-meta ${
                    l.id === article.locationId
                      ? 'bg-accent text-accent-ink'
                      : 'bg-surface-sunk text-ink'
                  }`}
                >
                  {l.emoji} {l.name}
                </button>
              ))}
            </div>
          </section>

          {/*
            Le RAYON se corrige ici, pas seulement depuis les réglages.
            La liste « à classer » ne montre que les produits qui n'en ont
            aucun ; depuis que le rayon est deviné à la création, le besoin
            courant devient de corriger une devinette, et c'est en voyant
            l'article qu'on s'en aperçoit.

            Ce n'est pas un détail d'affichage : le rayon donne l'emoji du
            produit quand il n'en a pas, et l'ordre de traversée du magasin.
          */}
          <section className="mb-5">
            <h3 className="mb-2 text-label uppercase text-muted">Rayon</h3>
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
              {rayons.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  aria-pressed={r.id === produit?.rayonId}
                  onClick={() =>
                    produit &&
                    magasin.dispatch('catalog.update', {
                      id: produit.id,
                      patch: { rayonId: r.id },
                    })
                  }
                  className={`pressable h-10 shrink-0 rounded-full px-3 text-meta ${
                    r.id === produit?.rayonId
                      ? 'bg-accent text-accent-ink'
                      : 'bg-surface-sunk text-ink'
                  }`}
                >
                  {r.emoji} {r.name}
                </button>
              ))}
            </div>
          </section>

          <section className="mb-5">
            <h3 className="mb-2 text-label uppercase text-muted">
              {lieuActuel?.isFreezer ? 'À consommer avant' : 'Date limite'}
            </h3>
            {dates.length > 0 && (
              <ul className="mb-2">
                {dates.map((d) => (
                  <li key={d.id} className="flex items-center justify-between border-b border-line py-2">
                    <span className="flex items-center gap-2">
                      <BadgeDlc date={d.date} maintenant={magasin.maintenant()} />
                      <span className="tnum text-body text-ink">{d.date}</span>
                      {joursAvant(d.date, magasin.maintenant()) < 0 && (
                        <span className="text-meta text-epuise-ink">dépassée</span>
                      )}
                    </span>
                    <button
                      type="button"
                      aria-label={`Retirer la date ${d.date}`}
                      onClick={() => magasin.dispatch('expiry.remove', { id: d.id })}
                      className="pressable text-meta text-muted"
                    >
                      <Croix className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                type="date"
                value={nouvelleDate}
                onChange={(e) => setNouvelleDate(e.target.value)}
                className="h-[48px] min-w-0 flex-1 rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none"
              />
              <button
                type="button"
                disabled={!nouvelleDate}
                onClick={() => {
                  magasin.dispatch('expiry.add', {
                    id: magasin.id(),
                    inventoryItemId: article.id,
                    date: nouvelleDate,
                  })
                  setNouvelleDate('')
                }}
                className="pressable h-[48px] shrink-0 rounded-sm bg-surface-sunk px-4 text-body text-ink disabled:opacity-40"
              >
                Ajouter
              </button>
            </div>
            {/* Plusieurs dates, volontairement : un congélateur contient trois
                barquettes du même plat congelées trois jours différents. */}
            {lieuActuel?.isFreezer && (
              <p className="mt-1.5 text-meta text-muted">
                Tu peux en mettre plusieurs — une par barquette.
              </p>
            )}
          </section>

          <section className="mb-6">
            <h3 className="mb-2 text-label uppercase text-muted">Note</h3>
            <input
              defaultValue={article.note ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim()
                if (v !== (article.note ?? '')) {
                  magasin.dispatch('item.setNote', { id: article.id, note: v || null })
                }
              }}
              placeholder="ex. entamé, au fond à gauche…"
              autoCorrect="off"
              enterKeyHint="done"
              className="h-[48px] w-full rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none"
            />
          </section>

          {/*
            FUSION — pas une suppression.

            Le scan crée des entrées très spécifiques (« Petits Pois Doux
            Très Fins CARREFOUR ») à côté des génériques (« Petits pois ») :
            sans fusion, l'inventaire se dédouble à chaque passage en caisse.
            Retirer le doublon perdrait son code-barres ; le fusionner le
            conserve, et le prochain scan tombe sur le bon produit.
          */}
          <section className="mb-6">
            <h3 className="mb-1 text-label uppercase text-muted">C'est un doublon ?</h3>
            <p className="mb-2 text-meta text-muted">
              Fusionne-le avec le produit générique. Le nom devient un synonyme, le code-barres
              est conservé.
            </p>
            <input
              value={fusion}
              onChange={(e) => setFusion(e.target.value)}
              placeholder="Chercher le produit à garder…"
              autoCorrect="off"
              className="h-[48px] w-full rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none"
            />
            {fusion.trim() && (
              <ul className="mt-1">
                {chercher(
                  candidats.map((p) => ({ ...p, nom: p.name })),
                  fusion,
                  4,
                ).map((c) => {
                  const cible = c as unknown as Produit
                  return (
                    <li key={cible.id}>
                      <button
                        type="button"
                        onClick={() => {
                          magasin.dispatch('catalog.merge', {
                            garder: cible.id,
                            absorber: produit.id,
                          })
                          setFusion('')
                          onFermer()
                          annoncer(`« ${produit.name} » fusionné dans « ${cible.name} »`)
                        }}
                        className="pressable flex h-11 w-full items-center gap-2 rounded-sm px-2 text-left text-body text-ink"
                      >
                        <span aria-hidden>{cible.emoji ?? '·'}</span>
                        <span className="truncate">Garder « {cible.name} »</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <button
            type="button"
            onClick={supprimer}
            className="pressable mb-2 h-[52px] w-full rounded-sm bg-epuise-soft text-head text-epuise-ink"
          >
            Retirer de l'inventaire
          </button>
          <p className="mb-2 text-meta text-muted">
            « Retirer » veut dire qu'on ne suit plus ce produit. Si tu l'as simplement fini et
            qu'il t'en faut, mets-le plutôt sur « épuisé » : il partira aux courses.
          </p>
        </>
      )}
    </Sheet>
  )
}
